import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Worksheet {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
  thumbnail: string;
}

const worksheets: Worksheet[] = [
  // Polynomials & Polynomial Functions
  {
    id: 'g11-poly-ws-1',
    title: 'Polynomial Operations & Long Division',
    description: 'Practice adding, subtracting, multiplying polynomials and polynomial long division',
    topic: 'Polynomials',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://public.readdy.ai/ai/img_res/cc3d29830d102c2c23239bda700ea48e.jpg'
  },
  {
    id: 'g11-poly-ws-2',
    title: 'Synthetic Division & Remainder Theorem',
    description: 'Use synthetic division and apply the Remainder and Factor Theorems',
    topic: 'Polynomials',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=synthetic%20division%20table%20with%20numbers%20and%20arrows%20showing%20shortcut%20polynomial%20division%20method%20on%20white%20background%20educational%20algebra%20diagram%20clean%20style&width=400&height=300&seq=g11pw2&orientation=landscape'
  },
  {
    id: 'g11-poly-ws-3',
    title: 'Zeros of Polynomial Functions',
    description: 'Find rational zeros, apply Descartes Rule of Signs, and analyze end behavior',
    topic: 'Polynomials',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=polynomial%20function%20graph%20with%20labeled%20zeros%20crossing%20x%20axis%20showing%20multiplicity%20and%20end%20behavior%20with%20colorful%20annotations%20on%20white%20background%20educational%20style&width=400&height=300&seq=g11pw3&orientation=landscape'
  },
  {
    id: 'g11-poly-ws-4',
    title: 'Factoring Higher-Degree Polynomials',
    description: 'Factor sum/difference of cubes, factor by grouping, and fully factor degree-3 and degree-4 polynomials',
    topic: 'Polynomials',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=higher%20degree%20polynomial%20factoring%20showing%20sum%20of%20cubes%20difference%20of%20cubes%20formulas%20with%20colorful%20step%20by%20step%20breakdown%20on%20white%20background%20educational%20algebra%20poster&width=400&height=300&seq=g11pw4&orientation=landscape'
  },

  // Rational Expressions & Equations
  {
    id: 'g11-rat-ws-1',
    title: 'Simplifying Rational Expressions',
    description: 'Factor and simplify rational expressions, identify restrictions on the domain',
    topic: 'Rational Expressions',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=rational%20expression%20fraction%20with%20polynomial%20numerator%20and%20denominator%20being%20factored%20and%20simplified%20with%20colorful%20cancellation%20marks%20on%20white%20background%20educational%20algebra%20style&width=400&height=300&seq=g11rw1&orientation=landscape'
  },
  {
    id: 'g11-rat-ws-2',
    title: 'Multiplying & Dividing Rational Expressions',
    description: 'Multiply and divide rational expressions by factoring and canceling common factors',
    topic: 'Rational Expressions',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=two%20rational%20expression%20fractions%20being%20multiplied%20with%20factoring%20and%20cancellation%20steps%20shown%20with%20colorful%20arrows%20on%20white%20background%20educational%20math%20diagram&width=400&height=300&seq=g11rw2&orientation=landscape'
  },
  {
    id: 'g11-rat-ws-3',
    title: 'Adding & Subtracting Rational Expressions',
    description: 'Find LCD and add or subtract rational expressions with unlike denominators',
    topic: 'Rational Expressions',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=rational%20expressions%20addition%20with%20LCD%20least%20common%20denominator%20shown%20with%20colorful%20fractions%20being%20combined%20on%20white%20background%20educational%20algebra%20illustration&width=400&height=300&seq=g11rw3&orientation=landscape'
  },
  {
    id: 'g11-rat-ws-4',
    title: 'Solving Rational Equations',
    description: 'Solve rational equations and identify extraneous solutions',
    topic: 'Rational Expressions',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=rational%20equation%20being%20solved%20by%20multiplying%20both%20sides%20by%20LCD%20with%20extraneous%20solution%20check%20shown%20in%20colorful%20steps%20on%20white%20background%20educational%20math%20style&width=400&height=300&seq=g11rw4&orientation=landscape'
  },
  {
    id: 'g11-rat-ws-5',
    title: 'Asymptotes & Graphing Rational Functions',
    description: 'Find vertical, horizontal, and oblique asymptotes and sketch rational function graphs',
    topic: 'Rational Expressions',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=rational%20function%20graph%20with%20vertical%20and%20horizontal%20asymptotes%20shown%20as%20dashed%20lines%20with%20curve%20approaching%20them%20on%20coordinate%20plane%20white%20background%20educational%20style&width=400&height=300&seq=g11rw5&orientation=landscape'
  },

  // Exponential & Logarithmic Functions
  {
    id: 'g11-exp-ws-1',
    title: 'Exponential Growth & Decay',
    description: 'Model and solve exponential growth and decay problems including half-life and compound interest',
    topic: 'Exponential & Logarithms',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=exponential%20growth%20and%20decay%20curves%20on%20coordinate%20plane%20showing%20increasing%20and%20decreasing%20functions%20with%20labeled%20asymptotes%20on%20white%20background%20educational%20math%20diagram&width=400&height=300&seq=g11ew1&orientation=landscape'
  },
  {
    id: 'g11-exp-ws-2',
    title: 'Logarithm Properties & Expansion',
    description: 'Apply product, quotient, and power rules to expand and condense logarithmic expressions',
    topic: 'Exponential & Logarithms',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=logarithm%20properties%20product%20quotient%20power%20rules%20written%20with%20colorful%20formulas%20and%20examples%20on%20white%20background%20educational%20algebra%20poster%20clean%20design&width=400&height=300&seq=g11ew2&orientation=landscape'
  },
  {
    id: 'g11-exp-ws-3',
    title: 'Solving Exponential & Logarithmic Equations',
    description: 'Solve equations involving exponential and logarithmic expressions using properties and change of base',
    topic: 'Exponential & Logarithms',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=exponential%20equation%20being%20solved%20by%20taking%20logarithm%20of%20both%20sides%20with%20step%20by%20step%20solution%20shown%20in%20colorful%20organized%20format%20on%20white%20background%20educational%20style&width=400&height=300&seq=g11ew3&orientation=landscape'
  },
  {
    id: 'g11-exp-ws-4',
    title: 'Graphing Exponential & Log Functions',
    description: 'Graph transformations of exponential and logarithmic functions, identify asymptotes and key features',
    topic: 'Exponential & Logarithms',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=exponential%20and%20logarithmic%20function%20graphs%20shown%20as%20inverse%20reflections%20over%20y%20equals%20x%20line%20on%20coordinate%20plane%20with%20colorful%20labels%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g11ew4&orientation=landscape'
  },

  // Sequences, Series & Binomial Theorem
  {
    id: 'g11-seq-ws-1',
    title: 'Arithmetic Sequences & Series',
    description: 'Find terms, common differences, and sums of arithmetic sequences and series',
    topic: 'Sequences & Series',
    difficulty: 'Easy',
    pages: 3,
    thumbnail: 'https://readdy.ai/api/search-image?query=arithmetic%20sequence%20with%20equally%20spaced%20terms%20shown%20on%20number%20line%20with%20common%20difference%20labeled%20and%20sum%20formula%20on%20white%20background%20educational%20math%20illustration&width=400&height=300&seq=g11sw1&orientation=landscape'
  },
  {
    id: 'g11-seq-ws-2',
    title: 'Geometric Sequences & Series',
    description: 'Find terms, common ratios, and sums of finite and infinite geometric series',
    topic: 'Sequences & Series',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=geometric%20sequence%20with%20exponentially%20growing%20terms%20shown%20with%20common%20ratio%20arrows%20and%20sum%20formula%20on%20white%20background%20educational%20algebra%20diagram%20clean%20style&width=400&height=300&seq=g11sw2&orientation=landscape'
  },
  {
    id: 'g11-seq-ws-3',
    title: 'Sigma Notation & Series Sums',
    description: 'Evaluate sums written in sigma notation and convert between sigma and expanded form',
    topic: 'Sequences & Series',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=sigma%20notation%20symbol%20with%20index%20variable%20and%20upper%20lower%20bounds%20showing%20summation%20expansion%20with%20colorful%20terms%20on%20white%20background%20educational%20math%20poster&width=400&height=300&seq=g11sw3&orientation=landscape'
  },
  {
    id: 'g11-seq-ws-4',
    title: 'Binomial Theorem & Pascal\'s Triangle',
    description: 'Expand binomial expressions using the Binomial Theorem and Pascal\'s Triangle',
    topic: 'Sequences & Series',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=Pascal%20triangle%20with%20binomial%20coefficients%20highlighted%20and%20binomial%20expansion%20formula%20shown%20with%20colorful%20rows%20and%20arrows%20on%20white%20background%20educational%20algebra%20illustration&width=400&height=300&seq=g11sw4&orientation=landscape'
  },

  // Trigonometry & Unit Circle
  {
    id: 'g11-trig-ws-1',
    title: 'Unit Circle & Radian Measure',
    description: 'Convert between degrees and radians, identify coordinates on the unit circle',
    topic: 'Trigonometry',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=unit%20circle%20diagram%20with%20labeled%20angles%20in%20degrees%20and%20radians%20showing%20sine%20cosine%20coordinates%20at%20key%20angles%20on%20white%20background%20educational%20trigonometry%20poster&width=400&height=300&seq=g11tw1&orientation=landscape'
  },
  {
    id: 'g11-trig-ws-2',
    title: 'Trigonometric Identities',
    description: 'Verify and apply Pythagorean, reciprocal, quotient, and co-function identities',
    topic: 'Trigonometry',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=trigonometric%20identities%20list%20showing%20Pythagorean%20reciprocal%20quotient%20identities%20with%20colorful%20formulas%20organized%20in%20table%20on%20white%20background%20educational%20math%20reference%20sheet&width=400&height=300&seq=g11tw2&orientation=landscape'
  },
  {
    id: 'g11-trig-ws-3',
    title: 'Solving Trigonometric Equations',
    description: 'Solve trig equations over specified intervals and find general solutions',
    topic: 'Trigonometry',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=trigonometric%20equation%20being%20solved%20with%20unit%20circle%20reference%20showing%20multiple%20solutions%20in%20given%20interval%20with%20colorful%20annotations%20on%20white%20background%20educational%20style&width=400&height=300&seq=g11tw3&orientation=landscape'
  },
  {
    id: 'g11-trig-ws-4',
    title: 'Graphing Trig Functions & Transformations',
    description: 'Graph sine, cosine, and tangent functions with amplitude, period, phase shift, and vertical shift',
    topic: 'Trigonometry',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=sine%20and%20cosine%20wave%20graphs%20with%20labeled%20amplitude%20period%20phase%20shift%20and%20vertical%20shift%20on%20coordinate%20plane%20with%20colorful%20annotations%20on%20white%20background%20educational%20diagram&width=400&height=300&seq=g11tw4&orientation=landscape'
  },

  // Statistics & Probability
  {
    id: 'g11-stat-ws-1',
    title: 'Normal Distribution & Z-Scores',
    description: 'Interpret normal distributions, calculate z-scores, and use the empirical rule',
    topic: 'Statistics & Probability',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=normal%20distribution%20bell%20curve%20with%20standard%20deviations%20labeled%20and%20z%20score%20formula%20shown%20with%20shaded%20regions%20on%20white%20background%20educational%20statistics%20diagram&width=400&height=300&seq=g11st1&orientation=landscape'
  },
  {
    id: 'g11-stat-ws-2',
    title: 'Conditional Probability & Independence',
    description: 'Calculate conditional probability, test for independence, and use multiplication rules',
    topic: 'Statistics & Probability',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=probability%20tree%20diagram%20and%20Venn%20diagram%20showing%20conditional%20probability%20and%20independent%20events%20with%20colorful%20labels%20on%20white%20background%20educational%20statistics%20illustration&width=400&height=300&seq=g11st2&orientation=landscape'
  },
  {
    id: 'g11-stat-ws-3',
    title: 'Permutations & Combinations',
    description: 'Count arrangements using permutations and combinations with factorial notation',
    topic: 'Statistics & Probability',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=permutation%20and%20combination%20formulas%20with%20factorial%20notation%20and%20example%20problems%20showing%20counting%20arrangements%20on%20white%20background%20educational%20math%20poster%20clean%20design&width=400&height=300&seq=g11st3&orientation=landscape'
  },
  {
    id: 'g11-stat-ws-4',
    title: 'Data Analysis & Statistical Inference',
    description: 'Analyze data sets, interpret standard deviation, and draw conclusions from samples',
    topic: 'Statistics & Probability',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=statistical%20data%20analysis%20showing%20histogram%20scatter%20plot%20and%20standard%20deviation%20calculation%20with%20colorful%20charts%20on%20white%20background%20educational%20statistics%20diagram&width=400&height=300&seq=g11st4&orientation=landscape'
  },

  // Conic Sections
  {
    id: 'g11-conic-ws-1',
    title: 'Circles & Parabolas',
    description: 'Write equations of circles and parabolas in standard form and graph them',
    topic: 'Conic Sections',
    difficulty: 'Medium',
    pages: 4,
    thumbnail: 'https://readdy.ai/api/search-image?query=circle%20and%20parabola%20conic%20sections%20on%20coordinate%20plane%20with%20labeled%20center%20radius%20vertex%20focus%20directrix%20on%20white%20background%20educational%20geometry%20diagram&width=400&height=300&seq=g11cw1&orientation=landscape'
  },
  {
    id: 'g11-conic-ws-2',
    title: 'Ellipses & Hyperbolas',
    description: 'Write equations of ellipses and hyperbolas, identify vertices, foci, and asymptotes',
    topic: 'Conic Sections',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=ellipse%20and%20hyperbola%20conic%20sections%20on%20coordinate%20plane%20with%20labeled%20vertices%20foci%20and%20asymptotes%20shown%20with%20colorful%20annotations%20on%20white%20background%20educational%20math%20diagram&width=400&height=300&seq=g11cw2&orientation=landscape'
  },
  {
    id: 'g11-conic-ws-3',
    title: 'Completing the Square for Conics',
    description: 'Convert general form to standard form by completing the square for all conic sections',
    topic: 'Conic Sections',
    difficulty: 'Hard',
    pages: 5,
    thumbnail: 'https://readdy.ai/api/search-image?query=conic%20section%20equation%20being%20converted%20from%20general%20form%20to%20standard%20form%20by%20completing%20the%20square%20with%20colorful%20step%20by%20step%20algebra%20on%20white%20background%20educational%20style&width=400&height=300&seq=g11cw3&orientation=landscape'
  },
];

const fullReviewPacks = [
  {
    id: 'g11-review-1',
    title: 'Grade 11 Full Review Pack — Part 1',
    description: 'Comprehensive review of Polynomials, Rational Expressions, and Exponential & Logarithmic Functions',
    pages: 14,
    thumbnail: 'https://readdy.ai/api/search-image?query=grade%2011%20algebra%202%20review%20workbook%20cover%20with%20polynomial%20graphs%20rational%20functions%20and%20exponential%20curves%20in%20organized%20colorful%20layout%20on%20white%20background%20educational%20style&width=400&height=300&seq=g11rev1&orientation=landscape'
  },
  {
    id: 'g11-review-2',
    title: 'Grade 11 Full Review Pack — Part 2',
    description: 'Comprehensive review of Sequences & Series, Trigonometry, and the Unit Circle',
    pages: 14,
    thumbnail: 'https://readdy.ai/api/search-image?query=grade%2011%20precalculus%20review%20pack%20cover%20showing%20unit%20circle%20trigonometric%20graphs%20and%20series%20notation%20in%20colorful%20organized%20design%20on%20white%20background%20educational%20workbook&width=400&height=300&seq=g11rev2&orientation=landscape'
  },
  {
    id: 'g11-review-3',
    title: 'Grade 11 Full Review Pack — Part 3',
    description: 'Comprehensive review of Statistics, Probability, and Conic Sections',
    pages: 14,
    thumbnail: 'https://readdy.ai/api/search-image?query=statistics%20probability%20and%20conic%20sections%20review%20pack%20cover%20with%20bell%20curve%20ellipse%20hyperbola%20and%20probability%20tree%20in%20colorful%20layout%20on%20white%20background%20educational%20workbook%20design&width=400&height=300&seq=g11rev3&orientation=landscape'
  }
];

const topics = [
  'All Topics',
  'Polynomials',
  'Rational Expressions',
  'Exponential & Logarithms',
  'Sequences & Series',
  'Trigonometry',
  'Statistics & Probability',
  'Conic Sections',
];

const topicColors: Record<string, string> = {
  'Polynomials': 'bg-rose-100 text-rose-700',
  'Rational Expressions': 'bg-amber-100 text-amber-700',
  'Exponential & Logarithms': 'bg-teal-100 text-teal-700',
  'Sequences & Series': 'bg-violet-100 text-violet-700',
  'Trigonometry': 'bg-sky-100 text-sky-700',
  'Statistics & Probability': 'bg-green-100 text-green-700',
  'Conic Sections': 'bg-orange-100 text-orange-700',
};

export default function Grade11WorksheetsPage() {
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorksheets = worksheets.filter(ws => {
    const matchesTopic = selectedTopic === 'All Topics' || ws.topic === selectedTopic;
    const matchesSearch =
      ws.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ws.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTopic && matchesSearch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <Link to="/grade/11" className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">Grade 11</Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <span className="text-sm text-rose-600 font-medium">Worksheets</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Grade 11 Math Worksheets</h1>
              <p className="text-lg text-gray-600">Topic-specific printable drills for Algebra 2 / Pre-Calculus mastery</p>
            </div>
            <Link
              to="/grade/11"
              className="flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line text-xl"></i>
              View Curriculum
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link
            to="/grade/11"
            className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-rose-100 rounded-lg">
              <i className="ri-book-2-line text-2xl text-rose-600"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">Grade 11 Curriculum</div>
              <div className="text-sm text-gray-600">View all topics &amp; lessons</div>
            </div>
          </Link>

          <Link
            to="/grade-11-study-plan"
            className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg">
              <i className="ri-calendar-check-line text-2xl text-amber-600"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">Study Plan</div>
              <div className="text-sm text-gray-600">Week-by-week schedule</div>
            </div>
          </Link>

          <Link
            to="/grade-11-progress"
            className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg">
              <i className="ri-bar-chart-box-line text-2xl text-green-600"></i>
            </div>
            <div>
              <div className="font-semibold text-gray-900">Progress Tracker</div>
              <div className="text-sm text-gray-600">Track completed work</div>
            </div>
          </Link>
        </div>

        {/* Stats Banner */}
        <div className="bg-gradient-to-r from-rose-600 to-pink-600 rounded-xl p-6 mb-8 text-white">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <h2 className="text-xl font-bold mb-1">Grade 11 Worksheet Library</h2>
              <p className="text-rose-100 text-sm">Aligned with Common Core, NY Regents, Ohio, PA, VA &amp; TX standards</p>
            </div>
            <div className="flex gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold">{worksheets.length}</div>
                <div className="text-rose-200 text-sm">Worksheets</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">7</div>
                <div className="text-rose-200 text-sm">Topics</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">{worksheets.reduce((a, w) => a + w.pages, 0)}+</div>
                <div className="text-rose-200 text-sm">Pages</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              placeholder="Search worksheets by title or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
            />
          </div>
        </div>

        {/* Topic Filter Tabs */}
        <div className="mb-8 bg-white rounded-lg shadow-sm p-2">
          <div className="flex flex-wrap gap-2">
            {topics.map(topic => (
              <button
                key={topic}
                onClick={() => setSelectedTopic(topic)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  selectedTopic === topic
                    ? 'bg-rose-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-600 text-sm">
            Showing <span className="font-semibold text-gray-900">{filteredWorksheets.length}</span> worksheet{filteredWorksheets.length !== 1 ? 's' : ''}
            {selectedTopic !== 'All Topics' && (
              <span> in <span className="font-semibold text-rose-600">{selectedTopic}</span></span>
            )}
          </p>
          {(selectedTopic !== 'All Topics' || searchQuery) && (
            <button
              onClick={() => { setSelectedTopic('All Topics'); setSearchQuery(''); }}
              className="text-sm text-rose-600 hover:text-rose-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Worksheets Grid */}
        {filteredWorksheets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {filteredWorksheets.map(worksheet => (
              <div key={worksheet.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden group">
                <div className="h-48 overflow-hidden bg-gray-100">
                  <img
                    src={worksheet.thumbnail}
                    alt={worksheet.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${topicColors[worksheet.topic] || 'bg-gray-100 text-gray-700'}`}>
                      {worksheet.topic}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getDifficultyColor(worksheet.difficulty)}`}>
                      {worksheet.difficulty}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{worksheet.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{worksheet.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <i className="ri-file-text-line"></i>
                      <span>{worksheet.pages} pages</span>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                      <i className="ri-download-line"></i>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 mb-12">
            <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-file-search-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No worksheets found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter</p>
            <button
              onClick={() => { setSelectedTopic('All Topics'); setSearchQuery(''); }}
              className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Full Review Packs */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg">
              <i className="ri-book-3-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Full Review Packs</h2>
              <p className="text-gray-600 text-sm">Comprehensive multi-topic review worksheets</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fullReviewPacks.map(pack => (
              <div key={pack.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden group">
                <div className="h-48 overflow-hidden bg-gray-100">
                  <img
                    src={pack.thumbnail}
                    alt={pack.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="mb-3">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 whitespace-nowrap">
                      Full Review
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{pack.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{pack.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <i className="ri-file-text-line"></i>
                      <span>{pack.pages} pages</span>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-lg hover:from-rose-700 hover:to-pink-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                      <i className="ri-download-line"></i>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Topic Overview Cards */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse by Topic</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {topics.filter(t => t !== 'All Topics').map(topic => {
              const count = worksheets.filter(w => w.topic === topic).length;
              return (
                <button
                  key={topic}
                  onClick={() => { setSelectedTopic(topic); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer text-left group"
                >
                  <div className={`inline-block px-2 py-1 rounded-md text-xs font-medium mb-2 ${topicColors[topic]}`}>
                    {topic}
                  </div>
                  <div className="text-2xl font-bold text-gray-900 group-hover:text-rose-600 transition-colors">{count}</div>
                  <div className="text-xs text-gray-500">worksheets</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
